import {
  RECURRENCE_CONFIG,
  RECURRENCE_TYPE,
} from 'domain/value-objects/recurrence-type';

export const buildRecurrenceConfig = (values: {
  recurrencePushNotification?: { value: RECURRENCE_TYPE };
  customRecurrenceUnit?: { value: RECURRENCE_TYPE };
  customRecurrenceInterval?: number;
  weeklyDays?: number[];
  scheduledTo: Date;
}): RECURRENCE_CONFIG => {
  const config: RECURRENCE_CONFIG = {};

  const recurrenceType =
    values.recurrencePushNotification?.value === RECURRENCE_TYPE.CUSTOM
      ? values.customRecurrenceUnit?.value || RECURRENCE_TYPE.DAILY
      : values.recurrencePushNotification?.value ===
          RECURRENCE_TYPE.DAYS_OF_WEEK
        ? RECURRENCE_TYPE.WEEKLY
        : values.recurrencePushNotification?.value;

  switch (recurrenceType) {
    case RECURRENCE_TYPE.DAILY:
      if (values.recurrencePushNotification?.value === RECURRENCE_TYPE.CUSTOM) {
        config.dailyInterval = values.customRecurrenceInterval || 1;
      } else {
        config.dailyInterval = 1;
      }
      break;

    case RECURRENCE_TYPE.WEEKLY:
      if (values.recurrencePushNotification?.value === RECURRENCE_TYPE.CUSTOM) {
        config.weeklyInterval = values.customRecurrenceInterval || 1;
        config.weeklyDays = values.weeklyDays?.length
          ? values.weeklyDays
          : [values.scheduledTo.getDay()];
      } else if (
        values.recurrencePushNotification?.value ===
        RECURRENCE_TYPE.DAYS_OF_WEEK
      ) {
        config.weeklyInterval = 1;
        config.weeklyDays = values.weeklyDays?.length
          ? values.weeklyDays
          : [1, 2, 3, 4, 5];
      } else {
        config.weeklyInterval = 1;
        config.weeklyDays = values.weeklyDays?.length
          ? values.weeklyDays
          : [values.scheduledTo.getDay()];
      }
      break;

    case RECURRENCE_TYPE.MONTHLY:
      if (values.recurrencePushNotification?.value === RECURRENCE_TYPE.CUSTOM) {
        config.monthlyInterval = values.customRecurrenceInterval || 1;
      } else {
        config.monthlyInterval = 1;
      }
      config.monthlyDay = values.scheduledTo.getDate();
      break;

    case RECURRENCE_TYPE.YEARLY:
      if (values.recurrencePushNotification?.value === RECURRENCE_TYPE.CUSTOM) {
        config.yearlyInterval = values.customRecurrenceInterval || 1;
      } else {
        config.yearlyInterval = 1;
      }
      config.yearlyMonth = values.scheduledTo.getMonth() + 1;
      config.yearlyDay = values.scheduledTo.getDate();
      break;
  }

  return config;
};

export const calculateEndDateFromOccurrences = (
  startDate: Date,
  occurrences: number,
  recurrenceType: RECURRENCE_TYPE,
  customInterval?: number,
  customUnit?: RECURRENCE_TYPE,
): Date => {
  const endDate = new Date(startDate);

  if (occurrences <= 1) {
    return endDate;
  }

  const remainingOccurrences = occurrences - 1;

  switch (recurrenceType) {
    case RECURRENCE_TYPE.DAILY:
      endDate.setDate(endDate.getDate() + remainingOccurrences);
      break;

    case RECURRENCE_TYPE.WEEKLY:
      endDate.setDate(endDate.getDate() + remainingOccurrences * 7);
      break;

    case RECURRENCE_TYPE.MONTHLY:
      endDate.setMonth(endDate.getMonth() + remainingOccurrences);
      break;

    case RECURRENCE_TYPE.YEARLY:
      endDate.setFullYear(endDate.getFullYear() + remainingOccurrences);
      break;

    case RECURRENCE_TYPE.DAYS_OF_WEEK:
      const weekdays = [1, 2, 3, 4, 5];
      let daysAdded = 0;
      let currentDate = new Date(startDate);

      while (daysAdded < remainingOccurrences) {
        currentDate.setDate(currentDate.getDate() + 1);
        if (weekdays.includes(currentDate.getDay())) {
          daysAdded++;
        }
      }
      return currentDate;

    case RECURRENCE_TYPE.CUSTOM:
      const interval = customInterval || 1;
      const unit = customUnit || RECURRENCE_TYPE.DAILY;

      if (unit === RECURRENCE_TYPE.DAILY) {
        endDate.setDate(endDate.getDate() + remainingOccurrences * interval);
      } else if (unit === RECURRENCE_TYPE.WEEKLY) {
        endDate.setDate(
          endDate.getDate() + remainingOccurrences * interval * 7,
        );
      } else if (unit === RECURRENCE_TYPE.MONTHLY) {
        endDate.setMonth(endDate.getMonth() + remainingOccurrences * interval);
      } else if (unit === RECURRENCE_TYPE.YEARLY) {
        endDate.setFullYear(
          endDate.getFullYear() + remainingOccurrences * interval,
        );
      }
      break;

    default:
      return endDate;
  }

  return endDate;
};
